import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table, Skeleton } from "antd";
import {
    LiaChevronCircleDownSolid,
    LiaChevronCircleUpSolid,
} from "react-icons/lia";
import { ExpandableRow } from "./WidgetCellRenderers";
import { CommonWidgetMetrics, IRevstackPopupData, TableExportFormats } from "@ts/common-types";
import NoDataText from "./NoDataText";
import { REVSTACK_W7_ALL_CATEGORIES_KEYWORDS, REVSTACK_W7_TABLE_NO_DATA_MESSAGES } from "@/ts/constants";
import { buildRevstackTableHeader } from "@ts/revstack/ui-utils";
import { exportAntTableData, createColumnVisibilityMenu, competitionButtonHandler } from "@ts/revstack/helpers";
import { useSelector } from "react-redux";

interface IRevstackPerformanceTableProps {
    parentConfig?: any;
    config?: any;
    colDefs: any;
    originalColDefs?: any;
    expandedColumns: any;
    type?: "subcategory" | "regional" | "category";
    selectedCardName?: string;
    tableData: any;
    expandedTableData: any;
    isLoading?: boolean;
    isExpandable?: boolean;
    childFilters?: Array<any>;
    additionalFilters?: Array<any>;
    noPadding?: boolean;
    isTableOnlyMode?: boolean;
    isDrilldownTable?: boolean;
    isTitleRequired?: boolean;
    isLayoutButtonReq?: boolean;
    isCompetitionButtonReq?: boolean;
    isGroupedTable?: boolean;
    isDownloadButtonReq?: boolean;
    isCheckboxSelectionRequired?: boolean;
    isSearchReq?: boolean;
    isExpandRowsButtonReq?: boolean;
    showExpandTableHeader?: boolean;
    style?: {
        reduceHeightBy: any;
        height?: number;
        expandedHeight?: number;
    };
    searchHandler?: (e: React.ChangeEvent<HTMLInputElement>, data: any) => void;
    competitionButtonHandler?: () => void;
    createColumnVisibilityMenu?: () => void;
    downloadButtonHandler?: (extension: TableExportFormats) => void;
    searchValue?: string;
    columnVisibilityMap?: Record<string, boolean>;
    onColumnToggle?: (columnKey: string) => void;
    colVisibilityMode?: "group" | "columns";
    onTableSelection?: (selectedRows: any[]) => void;
    selectedRows?: any[];
}

interface IExpandIcon {
    expanded: boolean;
    onExpand: (
        record: any,
        e: React.MouseEvent<HTMLElement, MouseEvent>
    ) => void;
    record: any;
}

const ExpandIcon: React.FC<IExpandIcon> = ({ expanded, onExpand, record }) => (
    <span
        className="d-flex align-items-center justify-content-center"
        role="button"
        onClick={(e) => onExpand(record, e)}
    >
        {expanded ? (
            <LiaChevronCircleUpSolid size={20} style={{ color: "#919191" }} />
        ) : (
            <LiaChevronCircleDownSolid size={20} style={{ color: "#919191" }} />
        )}
    </span>
);

const NoDataTextStyle = { fontSize: "16px", fontWeight: "500" }


const RevstackPerformanceTable: React.FC<IRevstackPerformanceTableProps> = ({
    parentConfig,
    config,
    colDefs,
    originalColDefs,
    expandedColumns,
    type = "category",
    selectedCardName,
    tableData,
    expandedTableData,
    isLoading = false,
    isExpandable = true,
    childFilters,
    additionalFilters,
    noPadding = false,
    isTableOnlyMode = false,
    isGroupedTable = false,
    isTitleRequired = false,
    isLayoutButtonReq = false,
    isDownloadButtonReq = false,
    isCheckboxSelectionRequired = false,
    isCompetitionButtonReq = false,
    isSearchReq = false,
    isExpandRowsButtonReq = false,
    isDrilldownTable = false,
    showExpandTableHeader = true,
    style,
    searchHandler = () => { },
    searchValue = "",
    columnVisibilityMap = {},
    onColumnToggle = () => { },
    colVisibilityMode = "columns",
    onTableSelection,
    selectedRows: externalSelectedRows
}) => {
    const [selectedFilter, setSelectedFilter] = useState<string>(childFilters?.[0]?.defaultFilter || "");
    const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);
    const [expandAllRows, setExpandAllRows] = useState<boolean>(false);
    const [hideCompetition, setHideCompetition] = useState<boolean>(false);
    const [competitionFilteredData, setCompetitionFilteredData] = useState<any>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [loadedChunks, setLoadedChunks] = useState<number>(1);
    const [isExpandingRows, setIsExpandingRows] = useState<boolean>(false);
    const [shouldExpandAllVisible, setShouldExpandAllVisible] = useState<boolean>(false);
    const [maxExpandedRows] = useState<number>(8); // Limit to 8 expanded rows
    const [internalSelectedRows, setInternalSelectedRows] = useState<any[]>([]);
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);

    // Use external or internal state based on props
    const selectedRows = externalSelectedRows ?? internalSelectedRows;

    // Derive selectedRowKeys from selectedRows or use internal state
    const selectedRowKeys = useMemo(() => {
        // If we have external selectedRows, derive keys from it
        if (externalSelectedRows) {
            return externalSelectedRows.map(row => String(row.key));
        }
        // Otherwise use internal state
        return internalSelectedRowKeys;
    }, [externalSelectedRows, internalSelectedRowKeys]);
    const [priorityUpdateCounter, setPriorityUpdateCounter] = useState(0);
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);
    const expandTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dataSourceRef = useRef<any[]>([]);
    const intersectionObserver = useRef<IntersectionObserver | null>(null);


    const selectedChangeSku = useSelector((state: { showRevstackPopup: { value: IRevstackPopupData } }) => state.showRevstackPopup.value.changeSku.selectedChangeSku);

    const handleRowExpand = (expanded: boolean) => {
        setIsRowExpanded(expanded || false);
    };

    // Row selection handlers
    const handleRowSelectionChange = (newSelectedRowKeys: React.Key[], newSelectedRows: any[]) => {
        // If we're not using external control, update internal state
        if (!externalSelectedRows) {
            setInternalSelectedRows(newSelectedRows);
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }

        // Always notify parent of changes
        if (onTableSelection) {
            onTableSelection(newSelectedRows);
        }
    };

    const handleSelectAll = (selected: boolean, allSelectedRows: any[], changeRows: any[]) => {
        // will do something here
    };

    const handleSelectRow = (record: any, selected: boolean, allSelectedRows: any[]) => {
        // will do something here
    };

    // Row selection configuration
    const rowSelection = isCheckboxSelectionRequired ? {
        selectedRowKeys, // Always provide selectedRowKeys
        onChange: handleRowSelectionChange,
        onSelectAll: handleSelectAll,
        onSelect: handleSelectRow,
        type: 'checkbox' as const,
        preserveSelectedRowKeys: true,
        getCheckboxProps: (record: any) => ({
            name: record.name || record.title || record.id,
            // Optionally add disabled logic here if needed
            // disabled: record.someCondition,
        }),
    } : undefined;

    const handleCompetitionToggle = useCallback(() => {
        const filteredData = competitionButtonHandler(tableData, setHideCompetition);
        setCompetitionFilteredData(filteredData);
    }, [tableData]);

    /*
     !Note[DND]: Horizontal scroll synchronization between main table and expanded tables
     * This effect manages bidirectional scroll synchronization to ensure that when users
     * scroll horizontally in either the main table or any expanded table row, all tables
     * maintain the same scroll position. Uses specific CSS selectors to target only 
     * table-related scrollable containers within this component, preventing interference
     * with other scrollable elements. Includes automatic detection of dynamically added
     * expanded rows via MutationObserver.
     */

    useEffect(() => {
        const mainTable = tableRef.current;
        if (!mainTable) return;

        // Skip scroll sync setup when expanding rows to improve performance
        if (isExpandingRows) return;

        let isSyncing = false;
        const activeListeners: Array<() => void> = [];

        const findExpandedScrollableElements = (): HTMLElement[] => {
            const mainTableBody = mainTable.querySelector('.ant-table-body') as HTMLElement;
            if (!mainTableBody) return [];

            const elements = new Set<HTMLElement>();

            // Primary selectors for table containers
            const selectors = [
                '.ant-table-expanded-row .ant-table-content',
                '.ant-table-expanded-row .ant-table-body',
                '.revstack-expandable-table .ant-table-content',
                '.revstack-expandable-table .ant-table-body'
            ];

            //!Note[DND]: Collect elements from specific selectors
            selectors.forEach(selector => {
                mainTable.querySelectorAll(selector).forEach(el => {
                    const element = el as HTMLElement;
                    if (element !== mainTableBody) elements.add(element);
                });
            });

            //!Note[DND]: Optimized fallback: find scrollable elements within expanded rows
            if (elements.size === 0) {
                // Only check expanded rows, not all children
                mainTable.querySelectorAll('.ant-table-expanded-row .ant-table-body, .ant-table-expanded-row .ant-table-content').forEach(el => {
                    const element = el as HTMLElement;
                    if (element !== mainTableBody) {
                        elements.add(element);
                    }
                });
            }

            return Array.from(elements);
        };

        const createScrollHandler = (sourceElement: HTMLElement, targetElements: HTMLElement[]) => {
            return (e: Event) => {
                if (isSyncing) return;
                isSyncing = true;

                const scrollLeft = (e.target as HTMLElement).scrollLeft;
                targetElements.forEach(target => {
                    if (target !== e.target) target.scrollLeft = scrollLeft;
                });

                setTimeout(() => { isSyncing = false; }, 0);
            };
        };

        const addScrollListeners = (mainElement: HTMLElement, expandedElements: HTMLElement[]) => {
            //!Note[DND]: Remove existing listeners
            activeListeners.forEach(cleanup => cleanup());
            activeListeners.length = 0;

            const mainHandler = createScrollHandler(mainElement, expandedElements);
            mainElement.addEventListener('scroll', mainHandler);
            activeListeners.push(() => mainElement.removeEventListener('scroll', mainHandler));

            expandedElements.forEach(element => {
                const expandedHandler = createScrollHandler(element, [mainElement, ...expandedElements]);
                element.addEventListener('scroll', expandedHandler);
                activeListeners.push(() => element.removeEventListener('scroll', expandedHandler));
            });
        };

        const setupScrollSync = () => {
            const mainTableBody = mainTable.querySelector('.ant-table-body') as HTMLElement;
            if (!mainTableBody) return;

            const expandedElements = findExpandedScrollableElements();
            addScrollListeners(mainTableBody, expandedElements);

            // Retry if no expanded elements found, with faster retry during expand-all mode
            if (expandedElements.length === 0) {
                const retryDelay = shouldExpandAllVisible ? 200 : 500;
                setTimeout(setupScrollSync, retryDelay);
            }
        };

        //!Note[DND]: Check if element is table-related
        const isTableElement = (element: Element): boolean => {
            return element.classList.contains('ant-table-expanded-row') ||
                element.querySelector('.ant-table-expanded-row') !== null ||
                element.classList.contains('revstack-expandable-table') ||
                element.querySelector('.revstack-expandable-table') !== null;
        };

        //!Note[DND]: Debounced mutation observer to prevent excessive DOM queries
        let mutationTimeout: NodeJS.Timeout | null = null;
        const observer = new MutationObserver((mutations) => {
            // Debounce mutations to reduce frequency
            if (mutationTimeout) {
                clearTimeout(mutationTimeout);
            }

            mutationTimeout = setTimeout(() => {
                const shouldResync = mutations.some(mutation =>
                    mutation.type === 'childList' &&
                    [...mutation.addedNodes, ...mutation.removedNodes].some(node =>
                        node.nodeType === Node.ELEMENT_NODE && isTableElement(node as Element)
                    )
                );

                if (shouldResync) {
                    requestAnimationFrame(() => setupScrollSync());
                }
            }, 200); // Debounce for 200ms
        });

        setupScrollSync();
        observer.observe(mainTable, { childList: true, subtree: true });

        // Listen for expanded table render completion to update scroll sync
        const handleTableRendered = () => {
            setTimeout(setupScrollSync, 100);
        };
        window.addEventListener('expandedTableRendered', handleTableRendered);

        return () => {
            observer.disconnect();
            activeListeners.forEach(cleanup => cleanup());
            window.removeEventListener('expandedTableRendered', handleTableRendered);
            if (mutationTimeout) {
                clearTimeout(mutationTimeout);
            }
        };
    }, [isExpandingRows, expandedRowKeys.length]);

    //TODO: @sarvesh make it dynamic
    const isAllCategoriesCard = useMemo(() => {
        const cardNameLowerCase = selectedCardName?.toLowerCase();
        return REVSTACK_W7_ALL_CATEGORIES_KEYWORDS?.some((keyword: string) => cardNameLowerCase?.includes(keyword)) || false;
    }, [selectedCardName]);

    // Reduce height by the value passed via config - used to reduce table height to take into account table header or to reduce table height in general
    const reduceHeightBy = style?.reduceHeightBy ? style?.reduceHeightBy : 360;
    const [tableHeight, setTableHeight] = useState<number>(
        document?.documentElement?.clientHeight - reduceHeightBy || 0
    );

    useEffect(() => {
        if (isLoading) {
            setSelectedFilter(childFilters?.[0]?.defaultFilter || "");
        }
    }, [isLoading, childFilters]);

    // Reset competition filtered data when tableData changes
    useEffect(() => {
        setCompetitionFilteredData(null);
        setHideCompetition(false);
    }, [tableData]);

    useEffect(() => {
        if (isDrilldownTable) {
            const handleResize = () => {
                setTableHeight(document?.documentElement?.clientHeight - reduceHeightBy || 0);
            };

            window.addEventListener('resize', handleResize);

            handleResize();

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isDrilldownTable]);

    const computedFilterOptions = useMemo(() => {
        const primaryFilter = childFilters?.[0];
        if (!primaryFilter?.filterOptions || primaryFilter?.filterOptions?.length === 0) {
            return [];
        }

        return primaryFilter?.filterOptions?.map((option: any) => {
            let count = 0;

            if (primaryFilter?.type === "SELECT" && primaryFilter?.dataKey) {
                const filterKey = primaryFilter?.mappedDataKey || primaryFilter?.dataKey;
                if (option?.value === CommonWidgetMetrics.All) {
                    count = tableData?.length || 0;
                } else {
                    count = tableData?.filter((item: any) =>
                        item[filterKey] === option?.value
                    ).length || 0;
                }
            } else {
                // For radio filters, use direct key access
                count = tableData?.[option.value]?.length || 0;
            }

            return {
                ...option,
                count
            };
        });
    }, [tableData, childFilters]);

    const filteredData = useMemo(() => {
        const primaryFilter = childFilters?.[0];
        if (!primaryFilter) return tableData;

        const isDropdownFilter = primaryFilter?.type === "SELECT";
        if (!isDropdownFilter) return tableData;

        if (!primaryFilter?.dataKey || !tableData || selectedFilter === CommonWidgetMetrics.All) {
            return tableData;
        }

        const filterKey = primaryFilter?.mappedDataKey || primaryFilter?.dataKey;
        const filtered = tableData.filter((item: any) =>
            item[filterKey] === selectedFilter
        );

        return {
            ...tableData,
            filtered
        };
    }, [tableData, selectedFilter, childFilters]);

    const showNoData = useMemo(() => {
        if (isSearchReq && searchValue && searchValue.trim() !== "") {
            return false;
        }

        if (computedFilterOptions.length > 0) {
            const primaryFilter = childFilters?.[0];
            const isDropdownFilter = primaryFilter?.type === "SELECT";
            if (isDropdownFilter) {
                return (filteredData?.length || 0) === 0;
            }
            // For radio filters like laggards/leaders, check if selected filter has no data
            return (tableData?.[selectedFilter]?.length || 0) === 0;
        }
        return (tableData?.length || 0) === 0;
    }, [tableData, computedFilterOptions, filteredData, childFilters, selectedFilter, isSearchReq, searchValue]);

    const fullDataSource = useMemo(() => {
        // Use competition filtered data if available, otherwise use original data
        const baseData = competitionFilteredData || tableData;

        const result = computedFilterOptions?.length > 0
            ? (childFilters?.[0]?.type === "SELECT"
                ? (competitionFilteredData ? competitionFilteredData : filteredData) || []
                : baseData?.[selectedFilter] || [])
            : baseData || [];

        // Ensure we always return an array
        return Array.isArray(result) ? result : [];
    }, [computedFilterOptions, filteredData, childFilters, selectedFilter, tableData, competitionFilteredData]);

    // Chunked data source - replaces virtual scrolling for large datasets
    const dataSource = useMemo(() => {
        const hasLargeDataset = fullDataSource?.length > 50;
        const hasExpandedRows = expandedRowKeys.length > 0 || expandAllRows;

        // Use smaller chunks when rows are expanded to prevent UI blocking
        if (hasLargeDataset || hasExpandedRows) {
            // Reduce chunk size significantly when rows are expanded
            const chunkSize = hasExpandedRows ? 20 : 50;
            const itemsToShow = loadedChunks * chunkSize;
            return fullDataSource.slice(0, itemsToShow);
        }

        return fullDataSource;
    }, [fullDataSource, loadedChunks, expandedRowKeys.length, expandAllRows]);

    useEffect(() => {
        dataSourceRef.current = dataSource || [];
    }, [dataSource]);

    /*
     !Note[DND]: EXPANDED VIEW LOGIC OVERVIEW:
     * 
     * 1. ROW EXPANSION STATES:
     *    - expandedRowKeys: Array of currently expanded row keys
     *    - expandAllRows: Boolean flag for "expand all" operation
     *    - shouldExpandAllVisible: Triggers viewport-based progressive expansion
     * 
     * 2. PERFORMANCE OPTIMIZATIONS:
     *    - maxExpandedRows (8): Limits simultaneous expanded rows to prevent UI blocking
     *    - Viewport-based expansion: Only expands visible rows initially, others on scroll
     *    - TableRenderQueue: Serializes table rendering (max 2 concurrent) in ExpandableRow
     *    - IntersectionObserver: Detects when rows come into viewport for lazy expansion
     * 
     * 3. EXPANSION FLOW:
     *    a) User clicks "Expand All" → shouldExpandAllVisible = true
     *    b) getVisibleRowKeys() finds rows in viewport
     *    c) expandVisibleRows() expands them progressively (respects maxExpandedRows limit)
     *    d) Scroll listener triggers more expansions as user scrolls down
     *    e) ExpandableRow uses queue system to prevent simultaneous table renders
     * 
     * 4. KEY COMPONENTS:
     *    - generateRowKey(): Creates stable keys using row.id/row.key
     *    - expandedRowRender(): Renders ExpandableRow component for each expanded row
     *    - Scroll sync: Keeps horizontal scroll synchronized between main and expanded tables
     */

    const isUsingChunking = useMemo(() => {
        const hasLargeDataset = fullDataSource?.length > 50;
        const hasExpandedRows = expandedRowKeys.length > 0 || expandAllRows;
        return hasLargeDataset || hasExpandedRows;
    }, [fullDataSource?.length, expandedRowKeys.length, expandAllRows]);

    const generateRowKey = useCallback((row: any, index: number): string => {
        // For your data structure, use the key directly if it exists
        if (row?.key) return String(row.key);
        // Fallbacks if key doesn't exist
        if (row?.id) return String(row.id);
        return `row-${index}`;
    }, []);

    // Get currently visible row keys in viewport
    const getVisibleRowKeys = useCallback(() => {
        const tableContainer = tableRef.current;
        if (!tableContainer) return [];

        const scrollableElement = tableContainer.querySelector('.ant-table-body') as HTMLElement;
        if (!scrollableElement) return [];

        const containerRect = scrollableElement.getBoundingClientRect();
        const rows = scrollableElement.querySelectorAll('tbody tr[data-row-key]');

        const visibleKeys: React.Key[] = [];
        rows.forEach((row) => {
            const rowRect = row.getBoundingClientRect();
            // Check if row is visible (with some buffer)
            if (rowRect.bottom >= containerRect.top - 50 &&
                rowRect.top <= containerRect.bottom + 50) {
                const rowKey = row.getAttribute('data-row-key');
                if (rowKey) visibleKeys.push(rowKey);
            }
        });

        return visibleKeys;
    }, []);

    //!Note[DND]: Expand only visible rows for immediate response (respects maxExpandedRows limit)
    const expandVisibleRows = useCallback(() => {
        const visibleKeys = getVisibleRowKeys();
        setExpandedRowKeys(prev => {
            const newKeys = new Set([...prev, ...visibleKeys]);
            return Array.from(newKeys);
        });
    }, [getVisibleRowKeys]);

    /**
     * !Note[DND]:
     * * Setup IntersectionObserver for expand-all mode - tracks rows entering viewport
     * * Uses throttling to prevent excessive expansion requests
     */
    useEffect(() => {
        if (!shouldExpandAllVisible) return;

        const tableContainer = tableRef.current;
        if (!tableContainer) return;

        const scrollableElement = tableContainer.querySelector('.ant-table-body');
        if (!scrollableElement) return;

        // Create observer for rows becoming visible with light throttling
        let throttleTimeout: NodeJS.Timeout | null = null;
        intersectionObserver.current = new IntersectionObserver(
            (entries) => {
                // Light throttle to prevent spam but maintain responsiveness
                if (throttleTimeout) return;

                throttleTimeout = setTimeout(() => {
                    const newVisibleKeys: React.Key[] = [];

                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const rowElement = entry.target.closest('tr[data-row-key]') as HTMLElement;
                            if (rowElement) {
                                const rowKey = rowElement.getAttribute('data-row-key');
                                if (rowKey) {
                                    newVisibleKeys.push(rowKey);
                                }
                            }
                        }
                    });

                    // Limit total expanded rows and expand conservatively
                    if (newVisibleKeys.length > 0) {
                        setExpandedRowKeys(prev => {
                            // Only add keys if under the limit
                            if (prev.length >= maxExpandedRows) return prev;

                            const keysToAdd = newVisibleKeys
                                .slice(0, 1) // Only expand 1 row at a time
                                .filter(key => !prev.includes(key))
                                .slice(0, maxExpandedRows - prev.length); // Respect the limit

                            if (keysToAdd.length === 0) return prev;
                            return [...prev, ...keysToAdd];
                        });
                    }

                    throttleTimeout = null;
                }, 50); // Reduced to 50ms for better responsiveness
            },
            {
                root: scrollableElement,
                rootMargin: '50px 0px', // Reduced margin for more precise timing
                threshold: 0.1 // Lower threshold for earlier detection
            }
        );

        // Observe all table rows
        const observeRows = () => {
            const rows = scrollableElement.querySelectorAll('tbody tr[data-row-key]');
            rows.forEach(row => {
                if (intersectionObserver.current) {
                    intersectionObserver.current.observe(row);
                }
            });
        };

        // Initial observation
        setTimeout(observeRows, 100); // Small delay to ensure rows are rendered

        // Re-observe when new rows are added (chunking)
        const mutationObserver = new MutationObserver(() => {
            setTimeout(observeRows, 50);
        });

        mutationObserver.observe(scrollableElement, {
            childList: true,
            subtree: true
        });

        return () => {
            if (intersectionObserver.current) {
                intersectionObserver.current.disconnect();
            }
            mutationObserver.disconnect();
        };
    }, [shouldExpandAllVisible]);

    //!Note[DND]: Effect to handle expandAllRows state changes with viewport-based expansion
    useEffect(() => {
        // Clear any existing timeout
        if (expandTimeoutRef.current) {
            clearTimeout(expandTimeoutRef.current);
        }

        if (expandAllRows) {
            // Enable viewport-based expansion mode
            setShouldExpandAllVisible(true);

            // Immediately expand only visible rows
            expandTimeoutRef.current = setTimeout(() => {
                expandVisibleRows();
            }, 50); // Small delay to ensure UI is ready
        } else {
            // Disable expand-all mode and collapse all rows
            setShouldExpandAllVisible(false);
            setIsExpandingRows(false);
            setExpandedRowKeys([]);
        }
    }, [expandAllRows, expandVisibleRows]);

    /*
     !Note[DND]:
     * 
     * Reset chunks when data changes but preserve expanded row state
     * Reset scroll position when data actually changes (filters, etc.)
     * Don't reset expanded rows when just loading more chunks
     * Only reset when the actual data changes (filters, etc.)
     * 
     */

    useEffect(() => {
        setLoadedChunks(1);
        scrollPositionRef.current = 0;
    }, [fullDataSource, selectedFilter, hideCompetition]);

    //!Note[DND]: Infinite scroll effect for chunking (replaces virtual scrolling)
    useEffect(() => {
        if (!isUsingChunking) return;

        const tableContainer = tableRef.current;
        if (!tableContainer) return;

        let scrollTimeout: NodeJS.Timeout | null = null;

        const handleScroll = () => {
            // Debounce scroll events to improve performance
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }

            scrollTimeout = setTimeout(() => {
                const scrollableElement = tableContainer.querySelector('.ant-table-body') as HTMLElement;
                if (!scrollableElement) return;

                const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

                // Store current scroll position
                scrollPositionRef.current = scrollTop;

                // Be more conservative with loading when rows are expanded
                const hasExpandedRows = expandedRowKeys.length > 0 || expandAllRows;
                const threshold = hasExpandedRows ? 200 : 100; // Larger threshold when expanded
                const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold;

                // Don't load more chunks if currently expanding rows progressively
                if (isNearBottom && dataSource.length < fullDataSource.length && !isExpandingRows) {
                    // Use requestAnimationFrame to avoid disrupting expanded rows
                    requestAnimationFrame(() => {
                        setLoadedChunks(prev => prev + 1);
                    });
                }
            }, (expandedRowKeys.length > 0 || expandAllRows) ? 200 : 100); // Longer debounce when expanded
        };

        const scrollableElement = tableContainer.querySelector('.ant-table-body');
        if (scrollableElement) {
            scrollableElement.addEventListener('scroll', handleScroll);
            return () => {
                scrollableElement.removeEventListener('scroll', handleScroll);
                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                }
            };
        }
    }, [isUsingChunking, dataSource.length, fullDataSource.length, expandedRowKeys.length, expandAllRows, isExpandingRows]);

    //!Note[DND]: Preserve scroll position after chunk loading
    useEffect(() => {
        if (!isUsingChunking) return;

        const tableContainer = tableRef.current;
        if (!tableContainer) return;

        const scrollableElement = tableContainer.querySelector('.ant-table-body') as HTMLElement;
        if (scrollableElement && scrollPositionRef.current > 0) {
            // Restore scroll position after DOM update
            requestAnimationFrame(() => {
                scrollableElement.scrollTop = scrollPositionRef.current;
            });
        }
    }, [dataSource.length, isUsingChunking]);

    useEffect(() => {
        return () => {
            if (expandTimeoutRef.current) {
                clearTimeout(expandTimeoutRef.current);
            }

            if (intersectionObserver.current) {
                intersectionObserver.current.disconnect();
            }

            setSelectedFilter("");
            setIsRowExpanded(false);
            setExpandAllRows(false);
            setHideCompetition(false);
            setCompetitionFilteredData(null);
            setExpandedRowKeys([]);
            setLoadedChunks(1);
            setIsExpandingRows(false);
            setShouldExpandAllVisible(false);
            scrollPositionRef.current = 0;
            setTableHeight(document?.documentElement?.clientHeight - reduceHeightBy || 0);
            // Reset selection state only if using internal state
            if (!externalSelectedRows) {
                setInternalSelectedRows([]);
            }
        };
    }, []);

    // Listen for priority updates
    useEffect(() => {
        const handlePriorityUpdate = () => {
            setPriorityUpdateCounter(prev => prev + 1);
        };

        window.addEventListener('priorityUpdated', handlePriorityUpdate);
        return () => window.removeEventListener('priorityUpdated', handlePriorityUpdate);
    }, []);

    const originalColumnMenuItems = useMemo(() => {
        return createColumnVisibilityMenu(originalColDefs || colDefs, colVisibilityMode);
    }, [originalColDefs, colDefs, colVisibilityMode]);

    const modifiedColDefs = useMemo(() => {
        return colDefs?.map((cd: any, index: number) => {
            if (cd?.key === "drilldown_action") {
                return {
                    ...cd,
                    render: (value: any, record: any, index: number) => {
                        return cd.render(value, { ...record, dataSource: fullDataSource }, index);
                    }
                };
            }

            // Override render function for add_sku column to pass selectedChangeSku state
            if (cd?.key === "add_sku") {
                return {
                    ...cd,
                    render: (value: any, record: any) => {
                        return cd.render(value, record, selectedChangeSku);
                    }
                };
            }

            //!Note[DND]: This we have kep by default for gouped table, if its optional we can add this to config
            // Modify the first pinned column to include row count in header
            if (index === 0 && cd?.fixed === "left") {
                const rowCount = fullDataSource?.length || 0;

                // Handle different title types
                if (React.isValidElement(cd.title)) {
                    // If title is a React element (from TableTitleCellRenderer)
                    const titleText = cd.title.props.children || '';
                    return {
                        ...cd,
                        title: React.cloneElement(cd.title, {
                            ...cd.title.props,
                            children: `${titleText} (${rowCount})`
                        }),
                        // Force width constraints for fixed columns
                        width: cd.width,
                        minWidth: cd.width,
                        maxWidth: cd.width,
                        onHeaderCell: () => ({
                            style: {
                                width: `${cd.width}px`,
                                minWidth: `${cd.width}px`,
                                maxWidth: `${cd.width}px`,
                            }
                        }),
                        onCell: () => ({
                            style: {
                                width: `${cd.width}px`,
                                minWidth: `${cd.width}px`,
                                maxWidth: `${cd.width}px`,
                            }
                        })
                    };
                } else if (typeof cd.title === 'string') {
                    return {
                        ...cd,
                        title: `${cd.title} (${rowCount})`,
                        // Force width constraints for fixed columns
                        width: cd.width,
                        minWidth: cd.width,
                        maxWidth: cd.width,
                        onHeaderCell: () => ({
                            style: {
                                width: `${cd.width}px`,
                                minWidth: `${cd.width}px`,
                                maxWidth: `${cd.width}px`,
                            }
                        }),
                        onCell: () => ({
                            style: {
                                width: `${cd.width}px`,
                                minWidth: `${cd.width}px`,
                                maxWidth: `${cd.width}px`,
                            }
                        })
                    };
                }
            }

            // Enforce width constraints for any fixed column
            if (cd?.fixed && cd?.width) {
                return {
                    ...cd,
                    width: cd.width,
                    minWidth: cd.width,
                    maxWidth: cd.width,
                    onHeaderCell: () => ({
                        style: {
                            width: `${cd.width}px`,
                            minWidth: `${cd.width}px`,
                            maxWidth: `${cd.width}px`,
                        }
                    }),
                    onCell: () => ({
                        style: {
                            width: `${cd.width}px`,
                            minWidth: `${cd.width}px`,
                            maxWidth: `${cd.width}px`,
                        }
                    })
                };
            }

            return cd;
        });

    }, [colDefs, fullDataSource, selectedChangeSku]);

    const calculateTotalColumnWidth = useMemo(() => {
        if (!modifiedColDefs || modifiedColDefs.length === 0) {
            return 1500;
        }

        const totalWidth = modifiedColDefs.reduce((sum: number, col: any) => {
            const colWidth = col.width || col.minWidth || 120;
            return sum + colWidth;
        }, 0);

        return totalWidth;
    }, [modifiedColDefs]);

    const getExpandedTableScrollX = (isExpandedColsSameAsParent: boolean) => {
        return isExpandedColsSameAsParent ? calculateTotalColumnWidth : "max-content";
    };

    // Memoize expanded table data filtering to prevent expensive operations
    const expandedDataLookup = useMemo(() => {
        if (!expandedTableData || !Array.isArray(expandedTableData)) return new Map();

        const expandedTableKey = config?.config?.table?.expandedTableDataKey;
        if (!expandedTableKey) return new Map();

        // Group expandedTableData by the key for O(1) lookup instead of O(n) filtering
        const lookup = new Map<string, any[]>();
        expandedTableData.forEach((item: any) => {
            const keyValue = item[expandedTableKey]?.toString();
            if (keyValue) {
                if (!lookup.has(keyValue)) {
                    lookup.set(keyValue, []);
                }
                lookup.get(keyValue)!.push(item);
            }
        });
        return lookup;
    }, [expandedTableData, config?.config?.table?.expandedTableDataKey]);

    /*
    !Note[DND]: `shouldUseVirtual`
     * 1. USE CASES:
     *    - This enables the `antd` table to use virtual scrolling for large datasets
     *    - Works well with grouped tables, but not with non grouped tables
     * 2. CHALLENGES:
     *    - For non grouped tables, if we add fixed column width the in virtual mode its not getting fixed and causing the rows misalignment with the table column (zig-zag effect)
     *    - If we have expanded table with same columns as parent table, then we need to disable virtual scrolling, as its alignment is not inline wrt the parent table
     !    - Also in virtual mode the antd table skips the expanded rows while scrolling that primary reaason
     * 3. WORKAROUND:
     *    - For non grouped tables, we disabled virtual scrolling and added data chunking for the table
     * 
    */

    const shouldUseVirtual = useMemo(() => {
        const hasLargeDataset = fullDataSource?.length > 50;
        const hasExpandedRows = expandedRowKeys.length > 0 || expandAllRows;

        return hasLargeDataset && !hasExpandedRows && !isExpandingRows && isGroupedTable;
    }, [fullDataSource?.length, expandedRowKeys.length, expandAllRows, isExpandingRows, isDrilldownTable]);


    return (
        <div
            className="d-flex flex-column"
            style={{
                padding: noPadding ? "0" : "20px",
                height: noPadding ? "100%" : "auto",
                flex: noPadding ? 1 : "none",
            }}
        >
            {isLoading ? (
                <Skeleton.Node
                    className="w-100"
                    active
                    style={{ height: style?.height || 400 }}
                />
            ) : (
                <>
                    {buildRevstackTableHeader({
                        noPadding,
                        isSearchReq,
                        data: filteredData,
                        isCompetitionButtonReq,
                        isTitleRequired,
                        isLayoutButtonReq,
                        isDownloadButtonReq,
                        type,
                        selectedCardName,
                        childFilters,
                        additionalFilters,
                        computedFilterOptions,
                        setSelectedFilter,
                        layoutConfig: null,
                        searchHandler,
                        hideCompetition,
                        competitionButtonHandler: handleCompetitionToggle,
                        createColumnVisibilityMenu: () => originalColumnMenuItems,
                        downloadButtonHandler: (ext) => exportAntTableData(fullDataSource, modifiedColDefs, ext),
                        searchValue: searchValue,
                        isExpandRowsButtonReq,
                        expandRowsButtonHandler: () => setExpandAllRows(prev => !prev),
                        columnVisibilityMap,
                        onColumnToggle,
                    })}

                    {showNoData ? (
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                height: style?.height || 400,
                                border: noPadding
                                    ? "none"
                                    : "1px solid #e8e8e8",
                                borderRadius: noPadding ? "0 0 8px 8px" : "0",
                            }}
                        >
                            {/* TODO: @Sarvesh: These hardcoding have to be removed */}
                            {selectedFilter?.toLowerCase() === "leaders" ? (
                                <NoDataText
                                    message={
                                        isAllCategoriesCard
                                            ? REVSTACK_W7_TABLE_NO_DATA_MESSAGES
                                                .LEADERS.ALL_CATEGORIES
                                            : REVSTACK_W7_TABLE_NO_DATA_MESSAGES
                                                .LEADERS.SPECIFIC_CATEGORY
                                    }
                                    style={NoDataTextStyle}
                                />
                            ) : selectedFilter?.toLowerCase() === "laggards" ? (
                                <NoDataText
                                    message={
                                        isAllCategoriesCard
                                            ? REVSTACK_W7_TABLE_NO_DATA_MESSAGES
                                                .LAGGARDS.ALL_CATEGORIES
                                            : REVSTACK_W7_TABLE_NO_DATA_MESSAGES
                                                .LAGGARDS.SPECIFIC_CATEGORY
                                    }
                                    style={NoDataTextStyle}
                                />
                            ) : (
                                <NoDataText />
                            )}
                        </div>
                    ) : (
                        <>
                            {isGroupedTable || isDrilldownTable ? (
                                <div ref={tableRef}>
                                    <Table
                                        key={`grouped-table-${expandAllRows}`}
                                        className="revstack-grouped-table"
                                        bordered
                                        size="middle"
                                        tableLayout="fixed"
                                        virtual={shouldUseVirtual}
                                        columns={modifiedColDefs || []}
                                        dataSource={dataSource}
                                        rowKey={(row, idx) => generateRowKey(row, idx || 0)}
                                        rowClassName={(record) =>
                                            record?.isSection ? "section-row" : ""
                                        }
                                        rowSelection={rowSelection}
                                        scroll={{
                                            x: calculateTotalColumnWidth,
                                            y: isDrilldownTable
                                                ? tableHeight
                                                : isRowExpanded
                                                    ? style?.expandedHeight || 360
                                                    : style?.height || 400,
                                        }}
                                        pagination={false}
                                        {...(isExpandable && {
                                            expandable: {
                                                onExpand: handleRowExpand,
                                                expandedRowKeys,
                                                onExpandedRowsChange: (keys: readonly React.Key[]) => setExpandedRowKeys([...keys]),
                                                // Render expanded row content - uses ExpandableRow component with queue system
                                                expandedRowRender: (record: any) => {
                                                    const expandedTableKey = config?.config?.table?.expandedTableDataKey;
                                                    const expandedTableDataValue = record?.[expandedTableKey];
                                                    const isExpandedColsSameAsParent = expandedTableDataValue && expandedTableData?.length > 0;

                                                    // Use optimized lookup instead of expensive filtering
                                                    const drilldownData = isExpandedColsSameAsParent
                                                        ? expandedDataLookup.get(expandedTableDataValue?.toString()) || []
                                                        : record?.drilldown || [];

                                                    return (
                                                        <ExpandableRow
                                                            expandedColumns={expandedColumns}
                                                            record={isExpandedColsSameAsParent ? {
                                                                drilldown: drilldownData,
                                                            } : record}
                                                            showExpandTableHeader={showExpandTableHeader}
                                                            isExpandedColsSameAsParent={isExpandedColsSameAsParent}
                                                            scrollX={getExpandedTableScrollX(isExpandedColsSameAsParent)}
                                                        />
                                                    );
                                                },
                                                showExpandColumn: true,
                                                expandIconColumnIndex: 0,
                                                expandIcon: ExpandIcon,
                                                columnWidth: isGroupedTable
                                                    ? 50
                                                    : 15,
                                            },
                                        })}
                                        style={!isDrilldownTable ? {
                                            height: "390px",
                                        } : {}}
                                    />
                                </div>
                            ) : (
                                <Table
                                    rowKey={(row, idx) => generateRowKey(row, idx || 0)}
                                    className="revstack-table"
                                    size="middle"
                                    columns={modifiedColDefs}
                                    dataSource={dataSource}
                                    pagination={false}
                                    bordered={false}
                                    rowClassName={() => "align-middle"}
                                    rowSelection={rowSelection}
                                    style={{
                                        background: "transparent",
                                        fontSize: 14,
                                        width: "100%",
                                        tableLayout: "fixed",
                                        border: noPadding
                                            ? "none"
                                            : "1px solid #e8e8e8",
                                        borderRadius: noPadding
                                            ? "0 0 8px 8px"
                                            : "0",
                                        overflow: "hidden",
                                    }}
                                    scroll={{
                                        x: calculateTotalColumnWidth,
                                        y: isDrilldownTable
                                            ? tableHeight
                                            : isRowExpanded
                                                ? style?.expandedHeight || 360
                                                : style?.height || 400,
                                    }}
                                    {...(isExpandable && {
                                        expandable: {
                                            onExpand: handleRowExpand,
                                            expandedRowKeys,
                                            onExpandedRowsChange: (keys: readonly React.Key[]) => setExpandedRowKeys([...keys]),
                                            // Simple expanded row render for basic tables
                                            expandedRowRender: (record: any) => (
                                                <ExpandableRow
                                                    expandedColumns={expandedColumns}
                                                    record={record}
                                                    showExpandTableHeader={showExpandTableHeader}
                                                />
                                            ),
                                            showExpandColumn: true,
                                            expandIconColumnIndex: 0,
                                            expandIcon: ExpandIcon,
                                            columnWidth: 30,
                                        },
                                    })}
                                    components={{
                                        header: {
                                            cell: (props: any) => (
                                                <th
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: "50px",
                                                        whiteSpace: "normal",
                                                        textOverflow:
                                                            "ellipsis",
                                                        verticalAlign: "middle",
                                                        wordBreak: "break-word",
                                                        lineHeight: "1.2",
                                                        ...(isTableOnlyMode && {
                                                            padding: "0 20px",
                                                        }),
                                                    }}
                                                />
                                            ),
                                        },
                                        body: {
                                            cell: (props: any) => (
                                                <td
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        ...(isTableOnlyMode && {
                                                            paddingLeft: 20,
                                                            paddingRight: 35,
                                                        }),
                                                    }}
                                                />
                                            ),
                                            row: (props: any) => (
                                                <tr
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: "60px",
                                                    }}
                                                />
                                            ),
                                        },
                                    }}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default RevstackPerformanceTable;
